import React from 'react';
import { Dimensions, Text, View } from 'react-native';

import { useThemeColor } from '@/hooks/useThemeColor';

interface BarChartData {
  label: string;
  shortLabel?: string;
  value: number;
  color: string;
  showSeparatorAfter?: boolean; // Show separator line after this item
}

interface BarChartProps {
  data: BarChartData[];
  height?: number;
  showValues?: boolean;
}

export default function BarChart({ 
  data, 
  height = 200,
  showValues = true
}: BarChartProps) {
  const textColor = useThemeColor({}, 'text');
  const backgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  
  // Check if screen is narrow (less than 400px width)
  const screenWidth = Dimensions.get('window').width;
  const isNarrowScreen = screenWidth < 400;

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map(item => item.value), 1);
  const barHeight = Math.max(20, (height - 60) / data.length); // Reserve space for labels

  if (data.length === 0) {
    return (
      <View style={{ alignItems: 'center', padding: 20 }}>
        <View style={{
          width: '100%',
          height: height,
          backgroundColor: backgroundColor,
          justifyContent: 'center',
          alignItems: 'center',
          borderWidth: 1,
          borderColor: borderColor,
          borderRadius: 8,
        }}>
          <Text style={{ color: textColor, fontSize: 14, textAlign: 'center' }}>
            No data available
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={{ 
      width: '100%',
      alignSelf: 'stretch',
    }}>
      {data.map((item, index) => {
          const barWidth = (item.value / maxValue) * 100;
          
          return (
            <React.Fragment key={index}>
              <View 
                style={{ 
                  marginBottom: index < data.length - 1 ? 8 : 0,
                  height: barHeight,
                  width: '100%',
                }}
              >
                <View style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}>
                  {/* Label */}
                  <View style={{
                    width: isNarrowScreen ? 30 : 40,
                    marginRight: 8,
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Text 
                      style={{ 
                        fontSize: isNarrowScreen ? 12 : 14,
                        fontWeight: '600',
                        color: textColor,
                      }}
                      numberOfLines={1}
                    >
                      {item.shortLabel || item.label}
                    </Text>
                  </View>
                  
                  {/* Bar */}
                  <View style={{
                    flex: 1,
                    height: '100%',
                    backgroundColor: '#e9ecef',
                    borderRadius: 4,
                    overflow: 'hidden',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}>
                    <View style={{
                      width: `${barWidth}%`,
                      height: '100%',
                      backgroundColor: item.color,
                      borderRadius: 4,
                      justifyContent: 'center',
                      alignItems: 'flex-end',
                      paddingRight: 4,
                    }}>
                      {showValues && item.value > 0 && (
                        <Text 
                          style={{ 
                            fontSize: 11,
                            fontWeight: 'bold',
                            color: '#fff',
                            textShadowColor: 'rgba(0, 0, 0, 0.3)',
                            textShadowOffset: { width: 0, height: 1 },
                            textShadowRadius: 2,
                          }}
                        >
                          {item.value}
                        </Text>
                      )}
                    </View>
                  </View>
                </View>
              </View>
              {/* Separator line after item if specified */}
              {item.showSeparatorAfter && index < data.length - 1 && (
                <View style={{
                  height: 1,
                  backgroundColor: borderColor,
                  marginVertical: 8,
                  marginLeft: isNarrowScreen ? 38 : 48,
                  marginRight: 0,
                }} />
              )}
            </React.Fragment>
          );
        })}
    </View>
  );
}

